import {ScaleLinear} from 'd3';
import * as d3 from 'd3';
import React from 'react';

import {pluralize} from 'BenchlingUtil';
import * as selectioncontextmenu from 'components/SelectionContextMenu';
import {Sequence} from 'components/Sequence';
import {LayeringMode, PolymerLayout, SliceMapType} from 'constants/MapsConstants';
import {StrandOfDoubleStranded} from 'constants/MolbioConstants';
import FileStates from 'FileStates';
import {BioentityPopoverContent} from 'molbio/BioentityPopoverContent';
import getNucleotideAwareDisplayName from 'molbio/maps/getNucleotideAwareDisplayName';
import ComponentWrapper from 'molbio/maps/linear/ComponentWrapper';
import {LayersDict, timesPerSlice, whichDisplay} from 'molbio/maps/linear/LayeringHelpers';
import {
  getModificationMarginHeight,
  getSingleModificationHeight,
  getSinglePrimerInnerHeight,
  getTotalModificationHeight,
} from 'molbio/maps/PrimersViewConstants';
import Type from 'molbio/maps/Type';
import SequencePrimer from 'molbio/primers/backbone/SequencePrimer';
import {getPrimerShape} from 'molbio/primers/PrimerUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import Popover from 'ui-library/floating-ui/Popover';

function getLayerFill(
  primer: SequencePrimer,
  primerBasesVisible: boolean,
  primerSegmentIndex: number
): string {
  if (primerBasesVisible) {
    return d3.rgb(primer.color()).formatHex();
  } else {
    return `url(#stripe-${primer.id}-${primerSegmentIndex})`;
  }
}

function getLayerFillOpacity(primer: SequencePrimer, primerBasesVisible: boolean): number {
  if (primerBasesVisible) {
    if (primer.hover() || primer.selected()) {
      return 0.4;
    } else {
      return 0.2;
    }
  } else if (primer.hover() || primer.selected()) {
    return 1.0;
  } else {
    return 0.7;
  }
}

function getOverhangFill(
  primer: SequencePrimer,
  showOverhang: boolean,
  primerBasesVisible: boolean,
  width: number
): string {
  if (primer.hasOverhang() && showOverhang && !primerBasesVisible && width > 0) {
    return d3.rgb(primer.color()).darker().formatHex();
  } else {
    return 'none';
  }
}

interface PrimersViewProps {
  /** A function that takes the index of a residue and returns its x-axis position on the slice */
  xFn: ScaleLinear<number, number>;
  /** A function that takes the y-index of a part annotation and returns its y-axis position on the slice */
  yFn: ScaleLinear<number, number>;
  /** The index at which the slice starts */
  sliceStart: number;
  /** The index at which the slice ends */
  sliceEnd: number;
  /** The Backbone sequence being rendered */
  sequence: Sequence;
  /** The strand of the primers in this view */
  strand?: StrandOfDoubleStranded;
  /** A list of context menu option names to display when a primer is right-clicked */
  contextMenuOptions: Array<string>;
  /** Flat object layout data */
  polymerLayout: PolymerLayout;
  /** Whether or not primer bases are visible. */
  primerBasesVisible: boolean;
  /** Whether or not any of the primers on the slice have modifications */
  sliceHasAnyMods: boolean;
  /** A Type model; the type of the primer name */
  primerType: Type;
  /** A Type model; the type of the residues being annotated */
  residuesType: Type;
  /** What type of map this view is being drawn on */
  mapType: SliceMapType;
  /** A function that triggers a redraw of the slice wrapper. */
  redraw: () => void;
  /** The Primers that appear on this sequence. */
  slicePrimers: Array<SequencePrimer>;
  /** The ordinal y-indexes of the features on this layer. */
  layersDict: LayersDict;
  /** How to layer features. */
  layeringMode: LayeringMode;
}

export function PrimersView({
  xFn,
  yFn,
  sliceStart,
  sliceEnd,
  sequence,
  contextMenuOptions,
  polymerLayout,
  primerBasesVisible,
  sliceHasAnyMods,
  primerType,
  mapType,
  redraw,
  slicePrimers,
  layersDict,
  layeringMode,
}: PrimersViewProps): JSX.Element {
  redraw();

  return (
    <svg>
      <ComponentWrapper layout={polymerLayout} shouldStopMouseDownPropagation={true}>
        <>
          {slicePrimers.map((primer) => {
            const primerSegments = timesPerSlice<SequencePrimer>(
              primer,
              sliceStart,
              sliceEnd,
              primer.drawStart(sequence),
              primer.drawEnd(sequence)
            );
            return primerSegments.map((primerSegment, primerSegmentIndex) => {
              const {start, end} = whichDisplay(
                sliceStart,
                sliceEnd,
                primerSegment.drawStart(sequence),
                primerSegment.drawEnd(sequence),
                primerSegmentIndex,
                sequence.length()
              )!;

              const translateX = xFn(start);
              const translateY = yFn(layersDict[primerSegment.get(layeringMode)]);
              const dx = xFn(end) - xFn(start);
              const displayName = getNucleotideAwareDisplayName(primerSegment);
              const mismatches = primerSegment.mismatchedIndices(sequence);

              const {
                boxTransform,
                dashArray,
                strokeRightD,
                strokeRightRender,
                strokeLeftD,
                strokeLeftRender,
                strokeTopD,
                strokeBottomD,
                strokeMiddleRender,
                fillD,
                leftsideArrow,
                rightsideArrow,
                width,
                bodyHeight,
                showOverhang,
                textProps,
                reverseBaselessBoxTransform,
                overhangD,
              } = getPrimerShape(
                {
                  start: primerSegment.start(),
                  end: primerSegment.end(),
                  strand: primerSegment.strand(),
                  bases: primerSegment.bases(),
                  overhangEdge: primerSegment.overhangEdge(),
                  name: displayName,
                  // Handle primers bound to a sequence involved in an alignment. Currently, sequence alignment data includes
                  // a snapshot of the saved, bound primers but not a reference to each primer's ID and so they appear
                  // to be unsaved models on the frontend.
                  isNew:
                    primerSegment.get('_primer').isNew() &&
                    [SliceMapType.linear, SliceMapType.minimap].includes(mapType),
                },
                {
                  start,
                  end,
                  height: getSinglePrimerInnerHeight(primerType, sliceHasAnyMods, primerBasesVisible),
                  type: primerType,
                  singleModificationHeight: getSingleModificationHeight(primerType),
                  modificationMarginHeight: getModificationMarginHeight(primerType),
                  totalModificationHeight: getTotalModificationHeight(primerType),
                  sequenceLength: sequence.length(),
                  xFn,
                  sliceStart,
                  sliceEnd,
                  areBasesVisible: primerBasesVisible,
                  circular: sequence.circular(),
                  sequenceDrawStart: primerSegment.drawStart(sequence),
                  sequenceDrawEnd: primerSegment.drawEnd(sequence),
                  bindPosition: primerSegment.bindPosition(sequence),
                  mismatches,
                  dx,
                  modifications: primerSegment.modifiedNucleotides(),
                  nucleotideType: primerSegment.nucleotideType(),
                }
              );

              const strokedProps = {
                stroke: primerSegment.outlineColor(sequence),
                strokeWidth: primerSegment.mismatchedIndices(sequence).length > 0 ? 2 : 1,
                strokeOpacity: 0.7,
                fill: 'none',
                transform: boxTransform,
              };

              const dataTestKey =
                mismatches.length > 0
                  ? `(${mismatches.length} mismatched ${pluralize(mismatches.length, 'base')}) ${displayName}`
                  : displayName;

              // A rendered primer may not have an API id, e.g. if we're doing manual primer design and
              // displaying a dotted outline of where the primer would bind
              const primerApiId = primer.get('_primer').get('primer_api_id');

              return (
                <g
                  className="layer-group"
                  key={`${primerSegment.name()}-${primerSegmentIndex}`}
                  pointerEvents="all"
                  onMouseOver={() => {
                    primerSegment.set({hover: true});
                  }}
                  onMouseLeave={() => {
                    primerSegment.set({hover: false});
                  }}
                  onMouseDown={(e) => {
                    primerSegment.click(sequence, e.shiftKey);
                  }}
                  onDoubleClick={(e) => {
                    primerSegment.dblClick(sequence, e.shiftKey);
                  }}
                  onContextMenu={(e) => {
                    primerSegment.click(sequence, e.shiftKey);
                    selectioncontextmenu.open(e.nativeEvent, sequence, contextMenuOptions, {
                      primer,
                    });
                  }}
                >
                  <Popover
                    renderContent={() => (
                      <BioentityPopoverContent
                        id={primerApiId}
                        onClickName={() => {
                          if (primerApiId != null) {
                            FileStates.get().openFolderItem(primerApiId);
                          }
                        }}
                        validationExtras={null}
                      />
                    )}
                    isDisabled={primerApiId == null}
                  >
                    <g
                      className="layer"
                      transform={`translate(${translateX},${translateY})`}
                      {...getTestProps({
                        component: 'linearPrimersView',
                        element: 'primerRoot',
                        key: dataTestKey,
                      })}
                      data-amp-safe={false}
                    >
                      <defs>
                        <pattern
                          patternUnits="userSpaceOnUse"
                          width={14}
                          height={14}
                          id={`stripe-${primerSegment.id}-${primerSegmentIndex}`}
                        >
                          <rect
                            width={14}
                            height={14}
                            fill={d3.rgb(primerSegment.color().toString()).formatHex()}
                          />
                          <path
                            fill="black"
                            fillOpacity={0.15}
                            // FIXME: Where does this magic string come from?
                            d="M5.232,17.089l-3.536-3.535L16.268-1.018l3.536,3.536L5.232,17.089z"
                          />
                          <path
                            fill="black"
                            fillOpacity={0.15}
                            // FIXME: Where does this magic string come from?
                            d="M-1.839,10.018l-3.535-3.536L9.197-8.089l3.536,3.535L-1.839,10.018z"
                          />
                        </pattern>
                      </defs>
                      {/* Primer fill */}
                      <path
                        d={fillD}
                        fill={getLayerFill(primerSegment, primerBasesVisible, primerSegmentIndex)}
                        fillOpacity={getLayerFillOpacity(primerSegment, primerBasesVisible)}
                        transform={boxTransform}
                      />
                      {/* Left side stroke */}
                      <path
                        d={strokeLeftD}
                        shapeRendering={strokeLeftRender}
                        strokeDasharray={dashArray}
                        {...strokedProps}
                      />
                      {/* Right side stroke */}
                      <path
                        d={strokeRightD}
                        shapeRendering={strokeRightRender}
                        strokeDasharray={dashArray}
                        {...strokedProps}
                      />
                      {/* Top and bottom strokes */}
                      <path
                        d={`${strokeTopD}${strokeBottomD}`}
                        shapeRendering={strokeMiddleRender}
                        strokeDasharray={dashArray}
                        {...strokedProps}
                      />
                      {/* Hide arrows if we're showing bases */}
                      <path
                        d={leftsideArrow}
                        shapeRendering="geometricPrecision"
                        {...strokedProps}
                        {...(primerBasesVisible || width === 0 ? {strokeOpacity: 0} : {})}
                      />
                      <path
                        d={rightsideArrow}
                        shapeRendering="geometricPrecision"
                        {...strokedProps}
                        {...(primerBasesVisible || width === 0 ? {strokeOpacity: 0} : {})}
                      />
                      {textProps && (
                        <text
                          x={textProps.x}
                          y={textProps.y}
                          fontSize={textProps['font-size']}
                          fontWeight={primerSegment.selected() ? 'bold' : 'normal'}
                          transform={textProps.transform}
                          {...(textProps.fill != null ? {fill: textProps.fill} : {})}
                        >
                          {textProps.text}
                          {textProps.baseProps.map((baseProp, index) => (
                            <tspan x={baseProp.x} fill={baseProp.fill} key={`tspan-${index}`}>
                              {baseProp.text}
                            </tspan>
                          ))}
                        </text>
                      )}
                      {/* Hide overhang if we're showing bases */}
                      <path
                        shapeRendering="geometricPrecision"
                        transform={primerSegment.strand() === 1 ? boxTransform : reverseBaselessBoxTransform}
                        width={16}
                        height={bodyHeight}
                        d={overhangD}
                        fill={getOverhangFill(primerSegment, showOverhang, primerBasesVisible, width)}
                      />
                      {textProps?.modificationProps?.map((modProps, index) => (
                        <circle
                          cx={modProps.cx}
                          cy={modProps.cy}
                          r={modProps.r}
                          fill={modProps.fill}
                          shapeRendering={modProps.shapeRendering}
                        />
                      ))}
                    </g>
                  </Popover>
                </g>
              );
            });
          })}
        </>
      </ComponentWrapper>
    </svg>
  );
}

export function ForwardPrimersView(props: Omit<PrimersViewProps, 'strand'>): JSX.Element {
  return <PrimersView {...props} strand={1} />;
}

export function ReversePrimersView(props: Omit<PrimersViewProps, 'strand'>): JSX.Element {
  return <PrimersView {...props} strand={-1} />;
}
